import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/toast";
import { Vendor } from "../vendorConfig";
import { profileClient } from "@/shared/api/clients";
import { isAxiosError } from "axios";
import { useOrgId } from "../../../shared/auth/orgContext";
import { resetStationBodycamCache } from "@/features/bodycamMappings/api/bodycamMappingsQueries";

interface UseBodycamOnboardingOptions {
  initialVendorAlreadyOnboarded?: Vendor | null;
  onVendorChange?: (vendor: Vendor | null) => void;
}

export const useBodycamOnboarding = (
  stationIdOverride?: string,
  options?: UseBodycamOnboardingOptions,
) => {
  // Capture the initial value at mount time using a ref so skipInitialFetch
  // is stable and won't cause the effect to re-run on parent re-renders.
  const initialVendorRef = useRef(options?.initialVendorAlreadyOnboarded);
  const onVendorChangeRef = useRef(options?.onVendorChange);
  onVendorChangeRef.current = options?.onVendorChange;
  const skipInitialFetch = initialVendorRef.current !== undefined;

  const [activeStep, setActiveStep] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(!skipInitialFetch);
  const [success, setSuccess] = useState(false);
  const [vendorAlreadyOnboarded, setVendorAlreadyOnboarded] = useState<
    Vendor | null | undefined
  >(skipInitialFetch ? initialVendorRef.current : undefined);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const organizationId = useOrgId();
  const effectiveStationId = stationIdOverride ?? organizationId;

  const handleBackButtonClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (skipInitialFetch || !effectiveStationId) return;
    let canceled = false;
    setIsLoading(true);
    profileClient
      .get(`/v1/stations/${effectiveStationId}/integrations/bodycam/resolve`)
      .then((res) => {
        if (canceled) return;
        const vendorName = res.data.vendorName?.toUpperCase() as
          | Vendor
          | undefined;
        setVendorAlreadyOnboarded(vendorName ?? null);
      })
      .catch((error) => {
        if (canceled) return;
        setVendorAlreadyOnboarded(null);
        if (!isAxiosError(error) || error.response?.status !== 404) {
          toast.error(
            error.response?.data?.message ||
              "Could not load current bodycam integration.",
          );
        }
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [toast, effectiveStationId, skipInitialFetch]);

  const handleRemoveIntegration = useCallback(async () => {
    try {
      setIsRemoving(true);
      if (!effectiveStationId) {
        throw new Error("Could not determine station for this user.");
      }
      const csrftoken = Cookies.get("csrftoken");
      await profileClient.delete(
        `/v1/stations/${effectiveStationId}/integrations/bodycam/secrets`,
        {
          headers: { "X-CSRFToken": csrftoken },
        },
      );
      // The mappings panel is a sibling route in the same session, so its
      // cached roster would outlive the binding it came from.
      await resetStationBodycamCache(effectiveStationId);
      setVendorAlreadyOnboarded(null);
      onVendorChangeRef.current?.(null);
      setSelectedVendor(null);
      setSuccess(false);
      setIsRestarting(false);
      setActiveStep(0);
    } catch (error: unknown) {
      let errorMessage = "Could not remove integration. Please try again.";
      if (isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsRemoving(false);
      setIsRemoveDialogOpen(false);
    }
  }, [toast, effectiveStationId]);

  const handleSelectVendor = useCallback((vendor: Vendor) => {
    setSelectedVendor(vendor);
  }, []);

  const handleBack = useCallback(() => {
    if (isRestarting && vendorAlreadyOnboarded) {
      setIsRestarting(false);
      return;
    }
    if (activeStep === 1) setActiveStep(0);
  }, [isRestarting, vendorAlreadyOnboarded, activeStep]);

  const handleContinue = useCallback(() => {
    if (activeStep === 0 && selectedVendor) setActiveStep(1);
  }, [activeStep, selectedVendor]);

  const handleCredentialsSuccess = useCallback(
    async (vendor: Vendor) => {
      // Re-pointing the binding leaves the previous vendor's officer roster
      // cached for another 15 minutes; drop it before anything reads it. The
      // mappings panel mounts off this state, so publish the new binding only
      // once the removal has actually landed.
      if (effectiveStationId) {
        await resetStationBodycamCache(effectiveStationId);
      }
      setSuccess(true);
      setVendorAlreadyOnboarded(vendor);
      onVendorChangeRef.current?.(vendor);
      setIsRestarting(false);
    },
    [effectiveStationId],
  );

  const startUpdateFlow = useCallback((vendor: Vendor) => {
    setIsRestarting(true);
    setSelectedVendor(vendor);
    setActiveStep(1);
    setSuccess(false);
  }, []);

  return {
    // state
    activeStep,
    selectedVendor,
    isLoading,
    success,
    vendorAlreadyOnboarded,
    isRestarting,
    isRemoveDialogOpen,
    isRemoving,

    // handlers
    handleBackButtonClick,
    handleSelectVendor,
    handleContinue,
    handleBack,
    handleCredentialsSuccess,
    startUpdateFlow,
    setIsRemoveDialogOpen,
    handleRemoveIntegration,
  };
};
